import type { FrameioShareVideo } from '@/queries/frameio';
import { classifyQuotaError } from '@/lib/quota/classifyQuotaError';
import { isShareImportProRequired } from '@/lib/shareImport/shareImport';

const VIDEO_EXTENSION = /\.(mp4|webm|mov|mkv|avi|m4v|mxf)$/i;

export type FrameioImportSummary = {
  created: string[];
  /** Indices into `inputs` of the created items, so callers can map back to their own ids. */
  createdIndices: number[];
  /** Includes the input that triggered a quota/PRO stop. */
  failed: Array<{ name: string; error: unknown }>;
  skipped: string[];
  stoppedBy: 'quota' | 'pro' | null;
  stopError: unknown;
};

export function frameioVideoName(name: string): string {
  return name.replace(VIDEO_EXTENSION, '') || name;
}

export function sumSelectedDuration(videos: FrameioShareVideo[], selectedIds: ReadonlySet<string>): number {
  return videos.reduce((total, video) => (selectedIds.has(video.asset_id) ? total + video.duration_seconds : total), 0);
}

export function buildFrameioImportInputs<T extends object>({
  videos, selectedIds, baseInput,
}: {
  videos: FrameioShareVideo[];
  selectedIds: ReadonlySet<string>;
  baseInput: T;
}): Array<T & { name: string; base_video_url: string; duration_seconds: number }> {
  return videos
    .filter((video) => selectedIds.has(video.asset_id))
    .map((video) => ({
      ...baseInput,
      name: frameioVideoName(video.name),
      base_video_url: video.canonical_url,
      duration_seconds: video.duration_seconds,
    }));
}

function stopReason(error: unknown): 'quota' | 'pro' | null {
  if (isShareImportProRequired(error)) return 'pro';
  return classifyQuotaError(error) ? 'quota' : null;
}

/** Creates one project/library per input, in order. Ordinary failures are
 *  isolated; quota/PRO failures stop the batch because every remaining call
 *  would fail the same way. */
export async function runFrameioImport<I extends { name: string }>({
  inputs, create, onProgress,
}: {
  inputs: I[];
  create: (input: I) => Promise<void>;
  onProgress?: (current: number, total: number) => void;
}): Promise<FrameioImportSummary> {
  const summary: FrameioImportSummary = {
    created: [], createdIndices: [], failed: [], skipped: [], stoppedBy: null, stopError: null,
  };
  for (let index = 0; index < inputs.length; index += 1) {
    const input = inputs[index];
    onProgress?.(index + 1, inputs.length);
    try {
      // Sequential on purpose: quota is checked per call on the backend.
      await create(input);
      summary.created.push(input.name);
      summary.createdIndices.push(index);
    } catch (error) {
      summary.failed.push({ name: input.name, error });
      const reason = stopReason(error);
      if (reason) {
        summary.stoppedBy = reason;
        summary.stopError = error;
        summary.skipped = inputs.slice(index + 1).map((item) => item.name);
        break;
      }
    }
  }
  return summary;
}
